import { computePosition, autoUpdate, offset, shift, arrow, flip, hide } from '@floating-ui/dom';
import { createDivIn, createHeaderIn, createListItemIn, createUnorderedListIn, setElementInnerHTML, translate } from './DomUtils.ts';
import { clientI18n } from '../ClientI18n.ts';
import { UIDeviceController } from '../ui_html/UIDeviceController.ts';
let autoUpdateCleanupCallback = null;
let tooltip = null;
let tooltipArrow = null;
let currentHoveredElement = null;
let currentContent = null;
let currentPosition = 'bottom';
const zoomContainerSelectors = [
    '.scene_room_left',
    '.scene_room_mid',
    '.scene_room_right'
];
const zoomContainers = zoomContainerSelectors.map((selector)=>document.querySelector(selector)).filter(Boolean);
const isAffectedByZoomBug = UIDeviceController.isUserOnSafariOrIOS();
export function initTooltips() {
    createTooltip();
    attachElementListenersToEjsTooltips();
}
export function createTooltipIn(element, tooltipText, position, fontSize, width) {
    var _tooltipText_options;
    const isTextAlreadyTranslated = tooltipText.key === 'strings:colonistCommon.utils.#buffer';
    const text = isTextAlreadyTranslated ? (_tooltipText_options = tooltipText.options) === null || _tooltipText_options === void 0 ? void 0 : _tooltipText_options.value : translate(tooltipText.key, tooltipText.options);
    element.setAttribute('data-tooltip', text);
    if (position != undefined) element.setAttribute('data-tooltip-position', position);
    if (width != undefined) element.setAttribute('data-tooltip-width', width);
    if (fontSize != undefined) element.setAttribute('data-tooltip-fontsize', fontSize);
    attachElementListeners(element);
}
export function updateTooltipText(element, tooltipText) {
    var _tooltipText_options;
    const isTextAlreadyTranslated = tooltipText.key === 'strings:colonistCommon.utils.#buffer';
    const text = isTextAlreadyTranslated ? (_tooltipText_options = tooltipText.options) === null || _tooltipText_options === void 0 ? void 0 : _tooltipText_options.value : translate(tooltipText.key, tooltipText.options);
    element.setAttribute('data-tooltip', text);
}
export function createListTooltipIn(element, content, position, fontSize, width) {
    const contentParsed = {
        listTitle: translate(content.listTitle.key, content.listTitle.options),
        list: clientI18n.tArray(content.list)
    };
    element.setAttribute('data-tooltip-list', JSON.stringify(contentParsed));
    if (position != undefined) element.setAttribute('data-tooltip-position', position);
    if (width != undefined) element.setAttribute('data-tooltip-width', width);
    if (fontSize != undefined) element.setAttribute('data-tooltip-fontsize', fontSize);
    attachElementListeners(element);
}
export function disableTooltip(element) {
    element.setAttribute('data-tooltip-hidden', 'true');
}
export function enableTooltip(element) {
    element.removeAttribute('data-tooltip-hidden');
}
export function hideTooltip() {
    if (tooltip == undefined) return;
    tooltip.classList.remove('visible');
    currentHoveredElement = null;
    if (autoUpdateCleanupCallback) {
        autoUpdateCleanupCallback();
        autoUpdateCleanupCallback = null;
    }
}
function attachElementListenersToEjsTooltips() {
    document.querySelectorAll('[data-tooltip]').forEach((element)=>{
        if (!(element instanceof HTMLElement)) return;
        attachElementListeners(element);
    });
}
function attachElementListeners(element) {
    if (element.hasAttribute('data-tooltip-listeners')) return;
    element.addEventListener('mouseover', (e)=>{
        if (element.getAttribute('data-tooltip-hidden') === 'true') return;
        showTooltip(element);
    });
    element.addEventListener('mouseout', (e)=>{
        hideTooltip();
    });
    element.setAttribute('data-tooltip-listeners', 'true');
}
function createTooltip() {
    const existingTooltip = document.getElementById('tooltip');
    if (existingTooltip != null) return;
    tooltip = document.createElement('div');
    tooltip.className = 'floating-tooltip';
    tooltipArrow = createDivIn(tooltip, 'floating-tooltip-arrow');
    currentContent = createDivIn(tooltip, 'tooltip-content');
    document.body.appendChild(tooltip);
}
function showTooltip(hoveredElement) {
    if (tooltip == undefined) return;
    if (currentContent == undefined) return;
    if (!shouldShowTooltip(hoveredElement)) return;
    currentHoveredElement = hoveredElement;
    currentPosition = getTooltipPlacement(hoveredElement);
    setElementInnerHTML(currentContent, {
        key: 'strings:colonistCommon.utils.emptyString'
    });
    const listContent = hoveredElement.getAttribute('data-tooltip-list');
    if (listContent) {
        try {
            const content = JSON.parse(listContent);
            createListContent(content);
        } catch (e) {
            currentContent.innerText = 'Error displaying tooltip';
        }
    } else {
        const tooltipText = hoveredElement.getAttribute('data-tooltip');
        if (tooltipText == undefined) return;
        currentContent.innerText = tooltipText;
    }
    tooltip.style.maxWidth = getTooltipWidth(hoveredElement);
    tooltip.style.padding = getTooltipPadding(hoveredElement);
    currentContent.style.fontSize = getTooltipFontSize(hoveredElement);
    tooltip.classList.add('visible');
    void updatePosition();
}
function shouldShowTooltip(element) {
    if (!isAffectedByZoomBug) return true;
    const isZoomed = zoomContainers.some((container)=>{
        const zoomValue = window.getComputedStyle(container).zoom;
        return zoomValue !== '1';
    });
    if (!isZoomed) return true;
    return !zoomContainers.some((container)=>container.contains(element));
}
async function updatePosition() {
    if (tooltip == undefined) return;
    if (currentHoveredElement == undefined) return;
    if (autoUpdateCleanupCallback) {
        autoUpdateCleanupCallback();
    }
    autoUpdateCleanupCallback = autoUpdate(currentHoveredElement, tooltip, async ()=>{
        try {
            var _middlewareData_hide;
            if (tooltip == undefined) return;
            if (tooltipArrow == undefined) return;
            if (currentHoveredElement == undefined) return;
            const tooltipPadding = 8;
            const positionOptions = {
                placement: currentPosition,
                middleware: [
                    offset(tooltipPadding),
                    flip({
                        padding: tooltipPadding
                    }),
                    shift({
                        padding: tooltipPadding
                    }),
                    arrow({
                        element: tooltipArrow
                    }),
                    hide()
                ]
            };
            const computeResult = await computePosition(currentHoveredElement, tooltip, positionOptions);
            const { x, y, placement, middlewareData } = computeResult;
            if ((_middlewareData_hide = middlewareData.hide) === null || _middlewareData_hide === void 0 ? void 0 : _middlewareData_hide.referenceHidden) {
                hideTooltip();
                return;
            }
            const moveTooltip = ()=>{
                if (tooltip == undefined) return;
                Object.assign(tooltip.style, {
                    left: `${x}px`,
                    top: `${y}px`
                });
            };
            moveTooltip();
            if (middlewareData.arrow == undefined) return;
            const { x: arrowX, y: arrowY } = middlewareData.arrow;
            const arrowSide = {
                top: 'bottom',
                right: 'left',
                bottom: 'top',
                left: 'right'
            }[placement.split('-')[0]];
            if (arrowSide == undefined) return;
            const moveArrow = ()=>{
                if (tooltipArrow == undefined) return;
                Object.assign(tooltipArrow.style, {
                    left: arrowX != null ? `${arrowX}px` : '',
                    top: arrowY != null ? `${arrowY}px` : '',
                    right: '',
                    bottom: '',
                    [arrowSide]: '-4px'
                });
            };
            moveArrow();
        } catch (error) {
            return;
        }
    });
}
function createListContent(content) {
    if (currentContent == undefined) return;
    createHeaderIn(currentContent, {
        key: 'strings:colonistCommon.utils.#buffer',
        options: {
            value: content.listTitle
        }
    }, 'tooltip-list-title', undefined, 'H4');
    const listContent = createUnorderedListIn(currentContent, 'tooltip-list');
    content.list.forEach((item)=>{
        createListItemIn(listContent, {
            key: 'strings:colonistCommon.utils.#buffer',
            options: {
                value: item
            }
        }, '');
    });
}
function getTooltipPlacement(element) {
    const position = element.getAttribute('data-tooltip-position');
    const validPositions = [
        'top',
        'top-start',
        'top-end',
        'bottom',
        'bottom-start',
        'bottom-end',
        'right',
        'right-start',
        'right-end',
        'left',
        'left-start',
        'left-end'
    ];
    if (!validPositions.includes(position)) return 'bottom';
    return position;
}
function getTooltipWidth(element) {
    const width = element.getAttribute('data-tooltip-width');
    const validWidths = [
        'small',
        'medium',
        'large'
    ];
    const widthEnum = validWidths.includes(width) ? width : 'medium';
    switch(widthEnum){
        case 'small':
            return '112px';
        case 'medium':
            return '224px';
        case 'large':
            return '304px';
    }
}
function getTooltipFontSize(element) {
    const fontSizeEnum = getTooltipFontSizeType(element);
    switch(fontSizeEnum){
        case 'small':
            return '10px';
        case 'medium':
            return '12px';
    }
}
function getTooltipPadding(element) {
    const fontSizeEnum = getTooltipFontSizeType(element);
    switch(fontSizeEnum){
        case 'small':
            return '4px';
        case 'medium':
            return '8px';
    }
}
function getTooltipFontSizeType(element) {
    const fontType = element.getAttribute('data-tooltip-fontsize');
    const validFontSizeTypes = [
        'small',
        'medium'
    ];
    const fontSizeTypeEnum = validFontSizeTypes.includes(fontType) ? fontType : 'medium';
    return fontSizeTypeEnum;
}
